import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import './styles.css';
import { api, onSessionExpired } from './lib/api.js';
import { signOutEverywhere } from './lib/signOut.js';
import { announceSignOut, isSignedOutHere, markSignedOut } from './lib/idleSession.js';
import { AutoLogoutGuard } from './components/AutoLogoutGuard.js';
import { Modal } from './components/Modal.js';
import { useFetch } from './lib/useFetch.js';
import { useLiveVersion } from './lib/useLiveRefresh.js';
import { readContactParam, writeContactParam } from './lib/contactUrl.js';
import { createSearchAsYouType } from './lib/searchAsYouType.js';
import {
  FORM_BUILDER_URL,
  clearViewParam,
  externalViewHref,
  readViewParam,
} from './lib/viewLink.js';
import { announce, practicePush } from './lib/desktopAlerts.js';
import { NoticeBell } from './components/NoticeBell.js';
import { SupportTelnyxPage, readTelnyxPath } from './components/SupportTelnyxPage.js';
import {
  NEW_NOTE,
  clearGroupParams,
  readGroupParam,
  setApptParams,
  setNoteParam,
} from './lib/apptLink.js';
import { badgeCount, sessionStart, writeLinkParams } from './lib/progressQueue.js';
import { NOTES_REMOTE, RULES_CHANGED, TASKS_CHANGED, TASKS_REMOTE } from './lib/liveEvents.js';
import { messagesUrl, useMessagesUnread } from './lib/messages.js';
import { usePartTwoSetup } from './lib/partTwoSetup.js';
import { clearSettingsParams, readSettingsParam } from './lib/settingsLink.js';
import { clearSignatureParam, readSignatureParam } from './lib/signatureLink.js';
import { syncSignature } from './lib/signature.js';
import { notifyPhotosChanged } from './lib/userPhotos.js';
import { ProviderProfileFrame } from './tabs/ProviderProfileFrame.js';
import { GroupNotePage } from './components/GroupNotePage.js';
import { queueOpenTarget } from './lib/sessionNoteRoute.js';
import { ProgressNotesTab } from './tabs/ProgressNotesTab.js';
import type { ProgressQueueRow, ProgressQueueView, ProgressToWriteRow } from '@ehr/shared';
import { isOpenTask } from '@ehr/shared';
import { sessionView } from './lib/sessionGate.js';
import { Loading } from './components/States.js';
/** What opening a note needs: a queue row, or a Dashboard card's note (ehr#485). */
type QueueNoteRef = Pick<
  ProgressQueueRow,
  'id' | 'contactId' | 'groupSessionId' | 'appointmentKey'
> &
  Partial<Pick<ProgressQueueRow, 'isGroup'>>;
import { useSession, DEV_USERS, canManage, displayName } from './auth/session.js';
import { relabel, useBranding } from './lib/branding.js';
import { NotificationLogView } from './tabs/NotificationLog.js';
import { FormRulesTab } from './tabs/FormRulesTab.js';
import { SendTab } from './tabs/SendTab.js';
import { SignoffTab } from './tabs/SignoffTab.js';
import { ContactsTab } from './tabs/ContactsTab.js';
import type { DrawerView } from './tabs/ContactDrawer.js';
import { TasksTab } from './tabs/TasksTab.js';
import { DashboardTab } from './tabs/DashboardTab.js';
import { BillingTab } from './tabs/BillingTab.js';
import { ReportsTab } from './tabs/ReportsTab.js';
import { SettingsTab } from './tabs/SettingsTab.js';
import { DataSettingsModal } from './tabs/DataSettingsModal.js';
import { OPEN_DATA_SETTINGS } from './lib/settingsEvents.js';
import { SettingsMegaMenu } from './tabs/SettingsMegaMenu.js';
import { ImportExportModal } from './tabs/ImportExportModal.js';
import { ConfigListModal, type ConfigEntity } from './tabs/ConfigListModal.js';
import { ContactTypesManager } from './tabs/ContactTypesManager.js';
import { StatusesManager } from './tabs/StatusesManager.js';
import { TaskStatusesManager } from './tabs/TaskStatusesManager.js';
import { StatusActionsModal } from './tabs/StatusActionsModal.js';
import { AccessMatrixModal } from './tabs/AccessMatrixModal.js';
import { TouchRulesModal } from './tabs/TouchRulesModal.js';
import { CptCodesModal } from './components/CptCodesModal.js';
import { AuditView } from './tabs/AdminViews.js';
import { UsersModal } from './tabs/UsersModal.js';
import { buttonShown, useDashboardView } from './lib/dashboardView.js';
import { useMyPhoto } from './lib/userPhotos.js';
import { useScrollLock } from './lib/scrollLock.js';
import { applyTitle } from './lib/tabTitles.js';

/** A live check skipped for now (no user yet, or a role without the screen). */
const notYet = () => Promise.reject<{ version: string }>(new Error('not now'));

// Customize Nav persistence (per-browser). Order is a list of surface keys;
// hidden is the set of keys the user has toggled off.
const NAV_ORDER_KEY = 'credify.nav.order.v1';
const NAV_HIDDEN_KEY = 'credify.nav.hidden.v1';
function readLS<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeLS(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / disabled storage */
  }
}

// Nav surfaces + SVG icons ported verbatim from the platform mockup
// (legacy/EHR-7-30-V27.html — SIDENAV_NAV_DEF + the `ico` map).
const S = (p: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`;
const ICONS: Record<string, string> = {
  // calendar#46 step 9 - Progress Notes (file with a pen).
  notes: S(
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/>',
  ),
  dashboard: S(
    '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>',
  ),
  schedule: S(
    '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  ),
  contacts: S(
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  ),
  forms_hub: S(
    '<rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><line x1="9" y1="15" x2="12" y2="15"/>',
  ),
  form_builder: S(
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>',
  ),
  tasks: S(
    '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
  ),
  messages: S(
    '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  ),
  billing: S(
    '<rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>',
  ),
  reports: S(
    '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
  ),
  settings: S(
    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  ),
};

interface Surface {
  key: string;
  label: string;
  external?: string;
  phase?: string;
}
const SURFACES: Surface[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'schedule', label: 'Schedule', external: 'https://calendar.credifyfast.com' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'notes', label: 'Progress Notes' }, // calendar#46 step 9
  { key: 'forms_hub', label: 'Forms Hub' },
  { key: 'form_builder', label: 'Form Builder', external: FORM_BUILDER_URL },
  { key: 'tasks', label: 'Tasks' },
  { key: 'messages', label: 'Messages', external: 'https://messages.credifyfast.com' },
  { key: 'billing', label: 'Billing', external: 'https://rcm.credifyfast.com' },
  { key: 'reports', label: 'Reports', external: 'https://reports.credifyfast.com' },
  { key: 'settings', label: 'Settings' },
];

// Forms Hub sub-tabs — order + icons mirror the mockup's #fh-tab-nav
// (Signoff Queue, Send, Form Rules — Notifications removed, #108).
const FORMS_HUB_TABS = [
  {
    key: 'signoff',
    label: 'Signoff Queue',
    el: <SignoffTab />,
    icon: '<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>',
    badge: 'signoff' as const,
    badgeTone: '',
  },
  {
    key: 'send',
    label: 'Send',
    el: <SendTab />,
    icon: '<path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/>',
  },
  // #108 - Notifications sub-tab removed per Taras.
  {
    key: 'rules',
    label: 'Form Rules',
    el: <FormRulesTab />,
    icon: '<rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><line x1="9" y1="15" x2="12" y2="15"/>',
    badge: 'rules' as const,
    badgeTone: 'green',
  },
] as {
  key: string;
  label: string;
  el: ReactNode;
  icon: string;
  badge?: 'signoff' | 'alert' | 'rules';
  badgeTone?: string;
}[];

/** Tab-nav badges (mockup: signoffBadge / alertBadge / rulesBadge) — light counts. */
function useFormsHubBadges() {
  const signoff = useFetch<{ form: string | null; packetId: string | null }[]>(
    () => api.get('/signoff/queue'),
    [],
  );
  const alerts = useFetch<{ type: string }[]>(() => api.get('/notifications'), []);
  const rules = useFetch<{ status: string }[]>(() => api.get('/rules'), []);
  // credifyfbs#313: recount as soon as a rule is saved, paused or deleted here.
  const refreshRules = rules.refresh;
  useEffect(() => {
    window.addEventListener(RULES_CHANGED, refreshRules);
    return () => window.removeEventListener(RULES_CHANGED, refreshRules);
  }, [refreshRules]);
  const alertCount = (alerts.data ?? []).filter(
    (n) => n.type === 'overdue' || n.type === 'escalated',
  ).length;
  return {
    // Badge mirrors the queue: every unsigned sign-off (ehr#494; practice forms included).
    signoff: (signoff.data ?? []).length,
    alert: alertCount,
    // credifyfbs#313: every saved rule, the number Saved Rules lists (paused ones too). It
    // counted active rules only, so one paused rule made the badge 5 beside 6 saved rules.
    rules: (rules.data ?? []).length,
  };
}

function FormsHub({
  startTab,
}: {
  /** ehr#486: the sub-tab to open on (a Dashboard link to Send or the Signoff Queue). */
  startTab?: string | null;
}) {
  const [tab, setTab] = useState(() =>
    FORMS_HUB_TABS.some((t) => t.key === startTab) ? (startTab as string) : 'signoff',
  );
  const counts = useFormsHubBadges();
  const { user } = useSession();
  const userKey = user?.id ?? user?.email ?? 'me';
  // ehr#278: this device and the server agree on the saved signature before a sign-off reads it
  // (a signature saved in another browser, or only in this one before). ehr#524: the editor
  // itself moved to your profile; the sign-offs here still read the signature.
  useEffect(() => {
    void syncSignature(userKey);
  }, [userKey]);
  return (
    <>
      <nav className="tab-nav" id="fh-tab-nav" role="tablist">
        {FORMS_HUB_TABS.map((t) => {
          const n = t.badge ? counts[t.badge] : 0;
          return (
            <button
              key={t.key}
              type="button"
              id={`fh-tab-${t.key}`}
              className={`tab-btn ${tab === t.key ? 'active' : ''}`}
              role="tab"
              aria-selected={tab === t.key}
              aria-controls="fh-panel"
              onClick={() => setTab(t.key)}
            >
              <span className="ico" aria-hidden dangerouslySetInnerHTML={{ __html: S(t.icon) }} />
              {t.label}
              {t.badge && n > 0 && (
                <span className={`tab-badge ${t.badgeTone ?? ''}`}>{n > 99 ? '99+' : n}</span>
              )}
            </button>
          );
        })}
        {/* ehr#524: My Signature moved to your own profile (About > Signature). */}
      </nav>
      {/* credifyfbs#306: the open sub-tab is a tabpanel named for its tab (Send, Signoff Queue),
          so it is plain which one is showing; the tab labels stay on screen above it. */}
      <main
        className="fh-content"
        id="fh-panel"
        role="tabpanel"
        aria-labelledby={`fh-tab-${tab}`}
        data-tab={tab}
      >
        {FORMS_HUB_TABS.find((t) => t.key === tab)?.el}
      </main>
    </>
  );
}

function Planned({ label, phase }: { label: string; phase?: string }) {
  return (
    <main className="content">
      <div className="card">
        <h2>{label}</h2>
        <p className="muted">
          This surface is on the v2.0 roadmap{phase ? ` (${phase}, ADR-0006)` : ''}. The backend is
          being built incrementally — see the build plans under <code>docs/phase-*.md</code>. The
          Contacts and Tasks surfaces are already wired to live APIs.
        </p>
      </div>
    </main>
  );
}

const chipInitials = (label: string): string => {
  const parts = label.replace(/—.*/, '').trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'U';
};

// Deterministic avatar colour (mirrors the mockup's coloured user avatars).
const CHIP_COLORS = [
  '#1a8a66',
  '#7c3aed',
  '#0c7be0',
  '#0e7490',
  '#d97706',
  '#c0392b',
  '#3b72d9',
  '#0f766e',
];
function chipColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return CHIP_COLORS[h % CHIP_COLORS.length]!;
}
const svg = (inner: string) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    dangerouslySetInnerHTML={{ __html: inner }}
  />
);
const ICON_CHECK = '<polyline points="20 6 9 17 4 12"/>';
const ICON_MANAGE =
  '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>';
const ICON_AUDIT =
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>';
const ICON_MAIL =
  '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>';
const ICON_CAL =
  '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>';
// Settings-menu row icons (mirrors the mockup's settings menu-card).
const ICON_USERS_MENU =
  '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>';
const ICON_MATRIX =
  '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="3" y1="9" x2="21" y2="9"/>';

interface OrgUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
}
const orgUserName = (u: OrgUser) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;

/**
 * User chip (V27 top-bar) — avatar + name + a "Switch user" dropdown. In dev it
 * lists the seeded DEV_USERS (x-dev-user). In prod an admin gets the real org
 * users and can "view as" any of them (x-impersonate-email); everyone sees a
 * "Back to my account" control while impersonating.
 */
function UserChip({
  onNavigate,
  open,
  setOpen,
  photo,
}: {
  onNavigate: (surface: string) => void;
  open: boolean;
  setOpen: (v: boolean | ((o: boolean) => boolean)) => void;
  /** ehr#60 - the signed-in user's Provider Profile photo, when there is one. */
  photo: string | null;
}) {
  const { user, loading, devEmail, impersonate, viewAs, stopViewingAs, impersonating } =
    useSession();
  const [q, setQ] = useState('');
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  // Load real org users for the prod Switch-user menu (as the real admin only).
  useEffect(() => {
    if (!open || !IS_PROD || impersonating) return;
    api
      .get<OrgUser[]>('/settings/users')
      .then(setOrgUsers)
      .catch(() => setOrgUsers([]));
  }, [open, impersonating]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const devLabel = DEV_USERS.find((u) => u.email === devEmail)?.label;
  const label = IS_PROD
    ? user
      ? displayName(user.name, user.email)
      : 'Sign in'
    : (devLabel ?? user?.email ?? 'Sign in');
  const chipSeed = user?.email ?? label;

  // Unified option list (dev seeds vs real org users), filtered by the search box.
  const options = (
    IS_PROD
      ? orgUsers.map((u) => ({ key: u.id, name: orgUserName(u), role: u.role, email: u.email }))
      : DEV_USERS.map((u) => ({
          key: u.email,
          name: u.label.replace(/\s*—.*/, ''),
          role: u.label.includes('—') ? (u.label.split('—')[1]?.trim() ?? '') : '',
          email: u.email,
        }))
  ).filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));
  const activeEmail = IS_PROD ? user?.email : devEmail;
  const select = (email: string) => {
    if (IS_PROD) {
      if (email !== user?.email) viewAs(email);
    } else impersonate(email);
    setOpen(false);
  };

  // ehr#665: no "Sign in" chip while the session is still being checked
  if (loading && !user) return null;

  return (
    <div className="user-chip-wrap" ref={ref}>
      <button className="user-chip" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="user-avatar sm" style={{ background: chipColor(chipSeed) }}>
          {photo ? <img src={photo} alt="" /> : chipInitials(label)}
        </span>
        <span className="user-chip-name">
          {impersonating ? `Viewing: ${label}` : label.replace(/\s*—.*/, '')}
        </span>
        <span className="user-chip-caret">{svg('<polyline points="6 9 12 15 18 9"/>')}</span>
      </button>
      {open && (
        <div className="user-menu" role="menu">
          {impersonating && (
            <button
              className="user-menu-item up-return"
              onClick={() => {
                stopViewingAs();
                setOpen(false);
              }}
            >
              <span className="user-menu-back">←</span>
              <span className="user-menu-item-text">
                <b>Back to my account</b>
                <small className="as-typed">
                  {displayName(user?.actorName, user?.actorEmail, 'admin')}
                </small>
              </span>
            </button>
          )}
          <div className="user-menu-head">Switch user</div>
          <div className="user-menu-search-wrap">
            <span className="ups-icon">
              {svg('<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>')}
            </span>
            <input
              className="user-menu-search"
              placeholder="Search users…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search users"
            />
          </div>
          <div className="user-menu-list">
            {options.length === 0 ? (
              <div className="user-menu-empty">
                {IS_PROD && user?.role !== 'admin'
                  ? 'Only admins can switch users.'
                  : 'No users match.'}
              </div>
            ) : (
              options.map((o) => (
                <button
                  key={o.key}
                  className={`user-menu-item ${o.email === activeEmail ? 'active' : ''}`}
                  onClick={() => select(o.email)}
                >
                  <span className="user-avatar sm" style={{ background: chipColor(o.email) }}>
                    {chipInitials(o.name)}
                  </span>
                  <span className="user-menu-item-text">
                    <b>{o.name}</b>
                    <small>{o.role}</small>
                  </span>
                  {o.email === activeEmail && <span className="check-icon">{svg(ICON_CHECK)}</span>}
                </button>
              ))
            )}
          </div>
          {/* ehr#98 (Chase 10-12): your own profile, where your time format now lives. */}
          <button
            className="user-menu-manage"
            onClick={() => {
              onNavigate('my-profile');
              setOpen(false);
            }}
          >
            {svg(
              '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
            )}{' '}
            My profile
          </button>
          <button
            className="user-menu-manage"
            onClick={() => {
              onNavigate('users');
              setOpen(false);
            }}
          >
            {svg(ICON_MANAGE)} Manage users
          </button>
          <button
            className="user-menu-manage"
            onClick={() => {
              onNavigate('audit');
              setOpen(false);
            }}
          >
            {svg(ICON_AUDIT)} Audit log
          </button>
          <div className="user-menu-divider" />
          <div className="user-menu-connect">
            <div className="user-menu-connect-item" title="Integration coming soon">
              {svg(ICON_MAIL)} Connect Email <span className="user-menu-soon">soon</span>
            </div>
            <div className="user-menu-connect-item" title="Integration coming soon">
              {svg(ICON_CAL)} Connect Calendar <span className="user-menu-soon">soon</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Surfaces({
  active,
  contactsQuery,
  onNavigate,
  openContact,
  onOpenContact,
  onOpenContactConsumed,
  openTaskId,
  onOpenGroupNote,
  onOpenNote,
  onWriteNote,
  onQueue,
  hubTab,
}: {
  active: string;
  contactsQuery: string;
  onNavigate: (surface: string) => void;
  openContact: { id: string; view?: string; record?: string } | null;
  onOpenContact: (id: string, view?: string) => void;
  onOpenContactConsumed: () => void;
  /** alertshub#7: a task to open on arrival (an Alerts Hub card's link). */
  openTaskId: string | null;
  onOpenGroupNote: (appointmentKey: string, focus: string | null) => void;
  onOpenNote: (row: QueueNoteRef, mode: 'review' | 'edit' | 'view') => void;
  onWriteNote: (row: ProgressToWriteRow) => void;
  onQueue: (q: ProgressQueueView) => void;
  /** ehr#486: the Forms Hub sub-tab a link asked for (Send, Signoff Queue). */
  hubTab: { key: string; n: number } | null;
}) {
  if (active === 'dashboard')
    return (
      <main className="content">
        <DashboardTab
          onNavigate={onNavigate}
          onOpenContact={onOpenContact}
          onOpenGroupNote={onOpenGroupNote}
          onOpenNote={(contactId, id) =>
            onOpenNote({ id, contactId, groupSessionId: null, appointmentKey: null }, 'view')
          }
        />
      </main>
    );
  if (active === 'notes')
    return (
      <main className="content">
        <ProgressNotesTab
          onOpenNote={onOpenNote}
          onWriteNote={onWriteNote}
          onNewNote={(contactId) => {
            // ehr#181: + New note opens the client's Clinical Notes on a new note (ehr#479: it
            // asks which appointment and what kind, as + Add new does).
            setNoteParam(NEW_NOTE);
            onOpenContact(contactId, 'clinical_notes');
          }}
          onChanged={onQueue}
        />
      </main>
    );
  if (active === 'contacts')
    return (
      <ContactsTab
        externalSearch={contactsQuery}
        openContact={openContact as { id: string; view?: DrawerView; record?: string } | null}
        onOpenContactConsumed={onOpenContactConsumed}
      />
    );
  if (active === 'tasks')
    return (
      <main className="content">
        <TasksTab openTaskId={openTaskId} />
      </main>
    );
  if (active === 'billing')
    return (
      <main className="content">
        <BillingTab />
      </main>
    );
  if (active === 'reports')
    return (
      <main className="content">
        <ReportsTab onOpenContact={onOpenContact} />
      </main>
    );
  if (active === 'settings')
    return (
      <main className="content">
        <SettingsTab />
      </main>
    );
  if (active === 'audit')
    return (
      <main className="content">
        <AuditView />
      </main>
    );
  // ehr#491: the bell's Notification log (it opened a placeholder)
  if (active === 'log')
    return (
      <main className="content">
        <NotificationLogView />
      </main>
    );
  if (active === 'forms_hub') return <FormsHub key={hubTab?.n ?? 0} startTab={hubTab?.key} />;
  const s = SURFACES.find((x) => x.key === active);
  return <Planned label={s?.label ?? 'Surface'} phase={s?.phase} />;
}

const ACCOUNT_KEYS = ['settings'];

// Production = not served from localhost. In prod the dev user-switcher is disabled
// (ALLOW_DEV_SIGNIN=0), so the sign-in screen must point at the real SSO login.
const IS_PROD =
  typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname);

/**
 * Unauthenticated screen. In prod: auto-redirect to the SSO IdP on 401 (no
 * session) — once per tab to avoid a loop — and show an access-pending notice on
 * 403 (signed in but no ehr account). In dev: the impersonation hint.
 */
function SignInScreen({ loading, authStatus }: { loading: boolean; authStatus: number | null }) {
  const loginUrl = `https://login.credifyfast.com/login?redirect=${encodeURIComponent(
    typeof window !== 'undefined' ? window.location.href : '',
  )}`;

  useEffect(() => {
    if (loading || !IS_PROD || authStatus === 403) return; // 403 must never loop back to login
    if (sessionStorage.getItem('ehr.sso.redirected')) return; // one-shot guard
    sessionStorage.setItem('ehr.sso.redirected', '1');
    window.location.href = loginUrl;
  }, [loading, authStatus, loginUrl]);

  if (loading) return <p className="muted">Checking session…</p>;

  if (IS_PROD && authStatus === 403) {
    return (
      <p className="muted">
        You’re signed in with Credify, but your ehr account isn’t active yet. Ask an administrator
        to grant you access (<strong>Settings → Users</strong>), then reload.
      </p>
    );
  }

  if (IS_PROD) {
    return (
      <>
        <p className="muted">Redirecting to Credify sign-in… if nothing happens, use the button.</p>
        <a className="btn primary" href={loginUrl}>
          Sign in with Credify
        </a>
      </>
    );
  }

  return (
    <p className="muted">
      Pick a user from the <strong>user chip</strong> (top-right) to explore. In production this is
      the shared <code>login.credifyfast.com</code> SSO session.
    </p>
  );
}

/** ehr#172 / ehr#364: the browser's own pieces for lib/signOut.ts (used by Log out and the expired window). */
function signOutNow(): Promise<void> {
  let local: Storage | undefined;
  let session: Storage | undefined;
  try {
    local = window.localStorage;
    session = window.sessionStorage;
  } catch {
    /* storage blocked */
  }
  // ehr#416: the other EHR tabs show "signed out in another tab" instead of carrying on.
  markSignedOut();
  announceSignOut(local ?? null, 'manual');
  return signOutEverywhere({
    postLogout: () => api.post('/auth/logout'),
    fetchFn: (...a) => fetch(...a),
    storage: { local, session },
    assign: (url) => window.location.assign(url),
    isProd: IS_PROD,
    origin: window.location.origin,
  });
}

/**
 * ehr#279 - the sign-in ran out while the page was open (the SSO cookie lasts a day). Ask the person
 * to sign in again in a NEW tab, so nothing typed here is lost, then re-check and carry on.
 */
function SessionExpiredDialog() {
  const [open, setOpen] = useState(false);
  const [still, setStill] = useState(false);
  const [checking, setChecking] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // ehr#416: not after this tab signed out on purpose (its own screen says so).
  useEffect(() => onSessionExpired(() => !isSignedOutHere() && setOpen(true)), []);
  if (!open) return null;
  const loginUrl = `https://login.credifyfast.com/login?redirect=${encodeURIComponent(window.location.href)}`;
  const check = () => {
    setChecking(true);
    api
      .get('/auth/me')
      .then(() => {
        setOpen(false);
        setStill(false);
      })
      .catch(() => setStill(true))
      .finally(() => setChecking(false));
  };
  return (
    <Modal
      title="Your sign-in has expired"
      onClose={() => setOpen(false)}
      className="session-expired-modal"
      actions={
        <>
          {/* ehr#364: leave instead (e.g. someone else is signing in on this computer) */}
          <button
            className="btn session-expired-signout"
            onClick={() => {
              setSigningOut(true);
              void signOutNow();
            }}
            disabled={signingOut}
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
          <button className="btn" onClick={check} disabled={checking || signingOut}>
            {checking ? 'Checking…' : 'I’ve signed in'}
          </button>
          <button
            className="btn primary"
            onClick={() => window.open(loginUrl, '_blank', 'noopener')}
          >
            Sign in again
          </button>
        </>
      }
    >
      <p>
        For security, Credify signs you out after a day. Sign in again in a new tab, then come back
        and choose <strong>I’ve signed in</strong>. Nothing on this page is lost: anything that
        didn’t save can be saved again. Or choose <strong>Sign out</strong> to leave.
      </p>
      {still && (
        <p className="session-expired-still" role="alert">
          Still signed out. Finish signing in in the other tab, then try again.
        </p>
      )}
    </Modal>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'U';
}

export function App() {
  const { user, loading, authStatus, impersonating } = useSession();
  // ehr#492: the practice's product name and word overrides, read once someone is signed in
  const branding = useBranding(user?.id ?? null);
  // credify-platform#306: a practice admin who has not finished Onboarding Part Two goes there once per
  // sign-in (then the Dashboard shows a banner); everyone else, or signup unreachable: nothing happens.
  usePartTwoSetup(user, impersonating, { redirect: true });
  // messaging#24 - unread messages on the Messages tab, and an alert when one arrives.
  const [msgAlert, setMsgAlert] = useState(false);
  const msgAlertTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const unreadMessages = useMessagesUnread(Boolean(user), () => {
    // alertshub#8: chime, and a desktop notice when the EHR is in the background (never the text)
    void practicePush().then((push) =>
      announce(
        [
          {
            title: 'New message in Messaging',
            body: 'Open Messages to read it.',
            tag: 'credify-messages',
          },
        ],
        push,
      ),
    );
    setMsgAlert(true);
    clearTimeout(msgAlertTimer.current);
    msgAlertTimer.current = setTimeout(() => setMsgAlert(false), 8000);
  });
  // messaging#15 - "View as" is EHR-only; Messaging is told who is being viewed so it can say so.
  const openMessages = () =>
    window.location.assign(
      messagesUrl(impersonating ? displayName(user?.name, user?.email) : null),
    );
  // ehr#60 - the Provider Profile photo in the top-bar chip and the side-nav avatar.
  const myPhoto = useMyPhoto(Boolean(user));
  // On load, honour a `?contact=<id>` deep-link — land on Contacts with the drawer open.
  // alertshub#7: or `?view=<tab>&record=<id>` from an Alerts Hub card (the Tasks tab on a task).
  const [viewLink] = useState(readViewParam);
  // alertshub#13: a hub card about a Form Builder share opens Form Builder.
  useEffect(() => {
    const href = externalViewHref();
    if (href) window.location.replace(href);
  }, []);
  const [active, setActive] = useState(() =>
    readContactParam()
      ? 'contacts'
      : viewLink?.view === 'email_settings'
        ? 'dashboard'
        : (viewLink?.view ?? 'dashboard'),
  );
  const [openTaskId] = useState(() =>
    viewLink?.view === 'tasks' ? (viewLink.record ?? null) : null,
  );
  useEffect(() => {
    if (viewLink) clearViewParam(); // one-shot: a refresh doesn't reopen it
  }, [viewLink]);
  // ehr#524 - your signature lives on your own profile (About > Signature; it was Forms Hub ->
  // My Signature). /?signature=edit (older links, the Calendar's) opens My profile with the
  // signature editor up; the profile's own Edit signature buttons open the editor over it.
  const [sigLink, setSigLink] = useState(readSignatureParam);
  const [telnyxPath] = useState(() => readTelnyxPath());
  const [myProfileOpen, setMyProfileOpen] = useState(sigLink);
  const [collapsed, setCollapsed] = useState(false);
  // #36 — the sidenav Tasks badge shows a count only when tasks are OVERDUE
  // (the mockup gates only the bell on unread/seen_by; the nav badge is overdue-
  // only). "Overdue" is a WHOLE-DAY test (due day before today), matching the
  // Tasks tab's Overdue filter — a task due *today* is not overdue, and no badge
  // shows when nothing is past due.
  const [overdueTasks, setOverdueTasks] = useState<number | null>(null);
  // ehr#60 - Dashboard View can hide the Notifications bell and Import / Export.
  const dashView = useDashboardView();
  const [headerSearch, setHeaderSearch] = useState('');
  const [contactsQuery, setContactsQuery] = useState('');
  // Deep-link into a contact's drawer — from another surface (e.g. a Dashboard row)
  // or restored from the URL on load. ContactsTab keeps `?contact=` in sync thereafter.
  const [openContact, setOpenContact] = useState<{
    id: string;
    view?: string;
    record?: string;
  } | null>(() => readContactParam());
  const handleOpenContact = (id: string, view?: string, record?: string) => {
    setOpenContact({ id, view, ...(record ? { record } : {}) });
    setActive('contacts');
  };
  // Leaving Contacts closes the drawer, so drop the deep-link param too.
  useEffect(() => {
    if (active !== 'contacts') writeContactParam(null);
  }, [active]);
  // calendar#46 step 8: ?group=<eventId>@<date> opens the group note workspace.
  const [groupLink, setGroupLink] = useState(readGroupParam);
  // ehr#622 (reopened 10-24): a new person shown (View as, Back to my account) drops the chart or
  // group workspace the person before had asked for; the page body below is keyed on the user,
  // so its lists and counts (Progress Notes' tiles) load fresh for them.
  const [shownFor, setShownFor] = useState<string | null>(user?.id ?? null);
  if ((user?.id ?? null) !== shownFor) {
    if (shownFor && user) {
      setOpenContact(null);
      setGroupLink(null);
    }
    setShownFor(user?.id ?? null);
  }
  const [kebabOpen, setKebabOpen] = useState(false);
  // ehr#172 - Log out: end the shared SSO session (the EHR clears the parent-domain
  // cookie itself; the login service is also told, best effort), drop any "view as"
  // impersonation, then go to the Credify sign-in page, which returns here after.
  const [loggingOut, setLoggingOut] = useState(false);
  const logOut = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setKebabOpen(false);
    await signOutNow();
  };
  // calendar#34 / #33 / #37 / ehr#331: /?settings=programs|locations|payors|payments[&popup=1] opens
  // that list on load.
  const [settingsLink] = useState(readSettingsParam);
  // Data & settings lists the Calendar opens straight onto (its own pop-up when popup=1).
  const dataSettingsLink =
    settingsLink?.key === 'locations' ||
    settingsLink?.key === 'payors' ||
    settingsLink?.key === 'payments'
      ? settingsLink.key
      : undefined;
  // #501 follow-up: a "Reconnect your account" card opens Data & settings → Email & calendar.
  const [settingsOpen, setSettingsOpen] = useState(
    !!dataSettingsLink || viewLink?.view === 'email_settings',
  );
  // Manage Settings mega-menu (V34) + which DataSettingsModal rail section to open,
  // + a labelled shell for pages still being built (per-page phases).
  const [megaOpen, setMegaOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<string | undefined>(
    dataSettingsLink ?? (viewLink?.view === 'email_settings' ? 'mail' : undefined),
  );
  const [settingsShell, setSettingsShell] = useState<{ name: string; note?: string } | null>(null);
  const [configModal, setConfigModal] = useState<{ entity: ConfigEntity; title: string } | null>(
    settingsLink?.key === 'programs' ? { entity: 'programs', title: 'Programs' } : null,
  );
  const [contactTypesOpen, setContactTypesOpen] = useState(false);
  const [statusesOpen, setStatusesOpen] = useState(false);
  const [taskStatusesOpen, setTaskStatusesOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  // ehr#60 - "Manage Users" is now the Users & Profile modal (was a page).
  const [usersOpen, setUsersOpen] = useState(settingsLink?.key === 'users');
  // ehr#486: a Forms Hub sub-tab key (the Dashboard's View all → 'send', a sign-off row →
  // 'signoff') opens Forms Hub on that tab; it used to open an empty "Surface" page.
  const [hubTab, setHubTab] = useState<{ key: string; n: number } | null>(null);
  useEffect(() => {
    if (active !== 'forms_hub') setHubTab(null);
  }, [active]);
  const navigate = (surface: string) => {
    if (FORMS_HUB_TABS.some((t) => t.key === surface)) {
      setHubTab((h) => ({ key: surface, n: (h?.n ?? 0) + 1 }));
      setActive('forms_hub');
      return;
    }
    if (surface === 'users') setUsersOpen(true);
    else if (surface === 'my-profile')
      setMyProfileOpen(true); // ehr#98: the chip's My profile
    else setActive(surface);
  };
  const [touchRulesOpen, setTouchRulesOpen] = useState(false);
  const [cptOpen, setCptOpen] = useState(false); // calendar#38
  // Customize Nav (V34) — per-browser sidebar order + hidden set, editable inline.
  const [ieOpen, setIeOpen] = useState(false);
  const [navEditing, setNavEditing] = useState(false);
  const [navOrder, setNavOrder] = useState<string[]>(() => readLS<string[]>(NAV_ORDER_KEY, []));
  const [navHidden, setNavHidden] = useState<string[]>(() => readLS<string[]>(NAV_HIDDEN_KEY, []));
  const navDragIdx = useRef<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);
  // Settings sidenav sub-menu (grouped settings, expands inline in the sidenav;
  // "Data & settings" opens the modal). Collapsed rail has no room → open the modal.
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  // ehr#231 - freeze the page behind every window opened from Manage settings.
  useScrollLock(
    settingsOpen ||
      !!settingsShell ||
      !!configModal ||
      contactTypesOpen ||
      statusesOpen ||
      taskStatusesOpen ||
      actionsOpen ||
      matrixOpen ||
      usersOpen ||
      touchRulesOpen ||
      ieOpen,
  );
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const toggleSettingsMenu = () => {
    if (collapsed) {
      setSettingsOpen(true);
      return;
    }
    setSettingsMenuOpen((o) => !o);
  };

  // ehr#624 - the tab title names the page shown (lib/tabTitles.ts: one map, "[Module] · Credify").
  // A Settings window over the page titles it Settings; a client's chart stays on Contacts. It runs on
  // first paint too, and stays on the page's title while the session check is in flight.
  const inSettingsWindow =
    settingsMenuOpen ||
    settingsOpen ||
    usersOpen ||
    matrixOpen ||
    !!settingsShell ||
    !!configModal ||
    contactTypesOpen ||
    statusesOpen ||
    taskStatusesOpen ||
    actionsOpen ||
    touchRulesOpen ||
    cptOpen;
  useEffect(() => {
    applyTitle({
      signedIn: loading || !!user,
      active: telnyxPath ? 'settings' : active,
      inSettings: inSettingsWindow,
    });
  }, [loading, user, telnyxPath, active, inSettingsWindow]);

  useEffect(() => {
    if (!kebabOpen) return;
    const onDown = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) setKebabOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [kebabOpen]);

  useEffect(() => {
    if (!settingsMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (settingsBtnRef.current?.contains(t) || settingsMenuRef.current?.contains(t)) return;
      setSettingsMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSettingsMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [settingsMenuOpen]);

  // Header client-search → jump to Contacts filtered by the query (V27 parity).
  // ehr#777 - it filters as you type: a short pause after the last key applies the query
  // (no Enter needed); Enter still applies at once, and clearing the box shows everyone.
  const headerSearchRef = useRef(
    createSearchAsYouType((q) => {
      setContactsQuery(q);
      if (q.trim()) setActive('contacts');
    }),
  );
  useEffect(() => () => headerSearchRef.current.cancel(), []);
  const runHeaderSearch = () => {
    headerSearchRef.current.flush(headerSearch);
    setActive('contacts');
  };

  // Overdue-task count powers the Tasks nav badge (#36). Use `done=false` — the
  // API's bool() only accepts 'true'/'false' (a bare `0` parses to undefined,
  // which DROPS the filter and returns completed tasks too, so the badge counted
  // long-done tasks with old due dates). `done=false` = MY not-done tasks
  // (doneAt IS NULL); `cancelled` (and a `done` STATUS whose doneAt hasn't been
  // written) still come back from done=false, and the Tasks tab treats BOTH as
  // closed (isClosed = status 'done' || 'cancelled'). Exclude both here so the
  // badge count matches the tab's Overdue filter exactly (#107).
  const refreshOverdue = useCallback(() => {
    if (!user) return;
    api
      .get<{ id: string; dueAt: string | null; doneAt?: string | null; status?: string }[]>(
        '/tasks?done=false',
      )
      .then((rows) => {
        if (!Array.isArray(rows)) return setOverdueTasks(null);
        // Whole-day boundary: overdue only when the due DAY is strictly before
        // today (so "due today" is excluded), 1:1 with the Tasks tab's Overdue
        // filter (isClosed + taskDueDiff<0) — otherwise the badge and tab disagree.
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        setOverdueTasks(
          rows.filter((r) => {
            // ehr#659: open by the one rule every task surface uses
            if (!isOpenTask(r) || !r.dueAt) return false;
            const d = new Date(r.dueAt);
            d.setHours(0, 0, 0, 0);
            return d.getTime() < startOfToday.getTime();
          }).length,
        );
      })
      .catch(() => setOverdueTasks(null));
  }, [user]);
  // Recount on load + navigation, and #107 live — whenever a task is created,
  // completed, reopened, or deleted anywhere (the api client fires this on any
  // /tasks mutation), so the sidebar badge updates without a refresh.
  useEffect(() => {
    refreshOverdue();
  }, [refreshOverdue, active]);
  useEffect(() => {
    const onChanged = () => refreshOverdue();
    window.addEventListener(TASKS_CHANGED, onChanged);
    return () => window.removeEventListener(TASKS_CHANGED, onChanged);
  }, [refreshOverdue]);
  // ehr#344 / #341 - a task added, completed or reassigned by a teammate (another tab, another
  // user): the tasks stamp moves, the badge recounts, and an open Tasks page reloads. Until the
  // user has loaded the check is skipped (a failed check), not answered with a blank stamp: the
  // first real stamp would read as a change and reload everything for nothing.
  useLiveVersion(
    () => (user ? api.get<{ version: string }>('/tasks/version') : notYet()),
    () => {
      refreshOverdue();
      window.dispatchEvent(new Event(TASKS_REMOTE));
    },
  );

  // calendar#46 step 9 - Progress Notes: shown to clinical roles only; the badge counts
  // notes waiting for my review plus my notes returned with changes, and (reminders#2) my
  // checked-out sessions with no note yet.
  const clinicalRole = ['admin', 'manager', 'clinician', 'supervisor'].includes(user?.role ?? '');
  const [notesBadge, setNotesBadge] = useState<number | null>(null);
  const refreshNotesBadge = useCallback(() => {
    if (!user || !clinicalRole) return;
    api
      .get<ProgressQueueView>('/progress-notes/queue')
      .then((q) => setNotesBadge(badgeCount(q)))
      .catch(() => setNotesBadge(null));
  }, [user, clinicalRole]);
  useEffect(() => {
    refreshNotesBadge();
  }, [refreshNotesBadge, active]);
  // ehr#343 - a note started, saved or signed elsewhere: the badge and an open Progress Notes
  // page reload.
  useLiveVersion(
    () =>
      user && clinicalRole ? api.get<{ version: string }>('/progress-notes/version') : notYet(),
    () => {
      refreshNotesBadge();
      window.dispatchEvent(new Event(NOTES_REMOTE));
    },
  );
  // A queue row opens the note: the author's own group drafts in the group workspace,
  // everything else (reviews included) in the client's Clinical Notes. ehr#689 (Jay 10-30): a
  // family / couples note goes to the chart too, which opens it on its family page on that
  // attendee (lib/sessionNoteRoute), never in the group workspace.
  const openQueueNote = (row: QueueNoteRef, mode: 'review' | 'edit' | 'view') => {
    if (queueOpenTarget(row, mode) === 'group-workspace' && row.appointmentKey) {
      setGroupLink({ appointmentKey: row.appointmentKey, focus: row.contactId });
      return;
    }
    setNoteParam(row.id);
    handleOpenContact(row.contactId, 'clinical_notes');
  };
  // reminders#2 - a To write row opens that session's note, like the Calendar's Progress
  // Note button: a group attendee in the group workspace, a 1:1 session in the client's
  // Clinical Notes (the note it has, or a new one filled from the session).
  const openWriteNote = (row: ProgressToWriteRow) => {
    if (row.isGroup) {
      setGroupLink({ appointmentKey: row.appointmentKey, focus: row.contactId });
      return;
    }
    setApptParams(writeLinkParams({ ...row, date: sessionStart(row.seriesStartsAt, row.dateKey) }));
    handleOpenContact(row.contactId, 'clinical_notes');
  };

  // ehr#458: a note's dictation window opens Data & settings on Clinical notes.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const section = (e as CustomEvent<{ section?: string }>).detail?.section;
      setSettingsSection(section);
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_DATA_SETTINGS, onOpen);
    return () => window.removeEventListener(OPEN_DATA_SETTINGS, onOpen);
  }, []);

  // Child surfaces (e.g. Show Columns' "Column Rules"/"Set org default") can ask
  // for a labelled settings shell without prop-drilling.
  useEffect(() => {
    const onShell = (e: Event) => {
      const d = (e as CustomEvent<{ name: string; note?: string }>).detail;
      if (d?.name) setSettingsShell({ name: d.name, note: d.note });
    };
    window.addEventListener('credify:settings-shell', onShell);
    return () => window.removeEventListener('credify:settings-shell', onShell);
  }, []);

  const mainSurfaces = SURFACES.filter(
    (s) => !ACCOUNT_KEYS.includes(s.key) && (s.key !== 'notes' || clinicalRole),
  );
  const userName = displayName(user?.name, user?.email, 'User');

  // Apply the user's saved order, appending any surfaces not yet in it; hidden
  // items drop out in normal mode but stay visible (dimmed) while editing.
  const orderedMain = (() => {
    const byKey = new Map(mainSurfaces.map((s) => [s.key, s]));
    const out: Surface[] = [];
    for (const k of navOrder) {
      const s = byKey.get(k);
      if (s) {
        out.push(s);
        byKey.delete(k);
      }
    }
    for (const s of mainSurfaces) if (byKey.has(s.key)) out.push(s);
    return out;
  })();
  const visibleMain = navEditing
    ? orderedMain
    : orderedMain.filter((s) => !navHidden.includes(s.key));

  const navReorder = (to: number) => {
    const from = navDragIdx.current;
    navDragIdx.current = null;
    if (from == null || from === to) return;
    const keys = orderedMain.map((s) => s.key);
    const [moved] = keys.splice(from, 1);
    keys.splice(to, 0, moved!);
    setNavOrder(keys);
    writeLS(NAV_ORDER_KEY, keys);
  };
  const navToggleHide = (key: string) => {
    const next = navHidden.includes(key) ? navHidden.filter((k) => k !== key) : [...navHidden, key];
    setNavHidden(next);
    writeLS(NAV_HIDDEN_KEY, next);
  };
  const navResetCustomize = () => {
    setNavOrder([]);
    setNavHidden([]);
    localStorage.removeItem(NAV_ORDER_KEY);
    localStorage.removeItem(NAV_HIDDEN_KEY);
  };

  const navItem = (s: Surface) => (
    <button
      key={s.key}
      className={`sidenav-item ${active === s.key ? 'active' : ''}`}
      onClick={() =>
        s.key === 'messages'
          ? openMessages()
          : s.external
            ? window.location.assign(s.external)
            : s.key === 'settings'
              ? setSettingsOpen(true)
              : setActive(s.key)
      }
      title={
        collapsed
          ? s.key === 'messages' && unreadMessages
            ? `${s.label} (${unreadMessages} unread)`
            : s.label
          : undefined
      }
    >
      <span className="ico" aria-hidden dangerouslySetInnerHTML={{ __html: ICONS[s.key] ?? '' }} />
      <span className="sidenav-item-label">{relabel(s.label, branding.labels)}</span>
      {s.key === 'tasks' && overdueTasks ? (
        <span className="sidenav-badge">{overdueTasks > 9 ? '9+' : overdueTasks}</span>
      ) : null}
      {s.key === 'messages' && unreadMessages ? (
        <span className="sidenav-badge" aria-label={`${unreadMessages} unread messages`}>
          {unreadMessages > 99 ? '99+' : unreadMessages}
        </span>
      ) : null}
      {s.key === 'notes' && notesBadge ? (
        <span className="sidenav-badge">{notesBadge > 9 ? '9+' : notesBadge}</span>
      ) : null}
      {s.phase && <span className="soon">soon</span>}
    </button>
  );

  // Customize Nav editable row — drag to reorder, eye toggles visibility.
  const navItemEditable = (s: Surface, i: number) => {
    const hidden = navHidden.includes(s.key);
    return (
      <div
        key={s.key}
        className={`sidenav-item editing${hidden ? ' nav-hidden' : ''}`}
        draggable
        onDragStart={() => (navDragIdx.current = i)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => navReorder(i)}
      >
        <span className="nav-drag-handle" aria-hidden>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="6" r="1.6" />
            <circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" />
            <circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" />
            <circle cx="15" cy="18" r="1.6" />
          </svg>
        </span>
        <span
          className="ico"
          aria-hidden
          dangerouslySetInnerHTML={{ __html: ICONS[s.key] ?? '' }}
        />
        <span className="sidenav-item-label">{relabel(s.label, branding.labels)}</span>
        <button
          type="button"
          className="nav-eye"
          aria-label={hidden ? `Show ${s.label}` : `Hide ${s.label}`}
          title={hidden ? 'Show' : 'Hide'}
          onClick={() => navToggleHide(s.key)}
        >
          {hidden ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="shell">
      <nav id="sidenav" className={collapsed ? 'collapsed' : ''}>
        <button
          className="sidenav-collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="sidenav-brand">
          <div className="sidenav-brand-mark">C</div>
          <div className="sidenav-brand-text">
            <div className="sidenav-brand-name">{branding.displayName.trim() || 'EHR'}</div>
            <div className="sidenav-brand-sub">Credify</div>
          </div>
        </div>
        <div className="sidenav-nav">
          {navEditing && (
            <div className="sidenav-customize-head">
              <span className="sidenav-customize-title">Customize Nav</span>
              <button type="button" className="sidenav-customize-btn" onClick={navResetCustomize}>
                Reset
              </button>
              <button
                type="button"
                className="sidenav-customize-btn primary"
                onClick={() => setNavEditing(false)}
              >
                Done
              </button>
            </div>
          )}
          {navEditing ? visibleMain.map((s, i) => navItemEditable(s, i)) : visibleMain.map(navItem)}
          <div className="sidenav-divider" />
          <div className="sidenav-section-label">Account</div>
          <button
            ref={settingsBtnRef}
            className={`sidenav-item ${settingsMenuOpen ? 'active' : ''}`}
            onClick={toggleSettingsMenu}
            aria-haspopup="menu"
            aria-expanded={settingsMenuOpen}
            title={collapsed ? 'Settings' : undefined}
          >
            <span
              className="ico"
              aria-hidden
              dangerouslySetInnerHTML={{ __html: ICONS.settings ?? '' }}
            />
            <span className="sidenav-item-label">Settings</span>
          </button>
          {settingsMenuOpen && (
            <div className="sidenav-sub" role="menu" ref={settingsMenuRef}>
              <div className="sidenav-sublabel">Manage users</div>
              <button
                className="sidenav-subitem"
                role="menuitem"
                onClick={() => {
                  setUsersOpen(true);
                  setSettingsMenuOpen(false);
                }}
              >
                {svg(ICON_USERS_MENU)}
                <span>Manage Users</span>
              </button>
              <button
                className="sidenav-subitem is-soon"
                role="menuitem"
                title="Access Rights Matrix — coming soon"
                onClick={() => setSettingsMenuOpen(false)}
              >
                {svg(ICON_MATRIX)}
                <span>Access Rights Matrix</span>
                <span className="soon">soon</span>
              </button>
              <button
                className="sidenav-subitem data"
                role="menuitem"
                onClick={() => {
                  setSettingsOpen(true);
                  setSettingsMenuOpen(false);
                }}
              >
                {svg((ICONS.settings ?? '').replace(/^<svg[^>]*>|<\/svg>$/g, ''))}
                <span>Data &amp; settings</span>
              </button>
            </div>
          )}
        </div>
        {user && (
          <div className="sidenav-bottom">
            <div
              className="sidenav-user"
              title={userName}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setPickerOpen((o) => !o);
              }}
            >
              <span className="user-avatar sm">
                {myPhoto ? <img src={myPhoto} alt="" /> : initials(userName)}
              </span>
              <div className="sidenav-user-info">
                <div className="sidenav-user-name">{userName}</div>
                <div className="sidenav-user-role">{user.role}</div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile bottom tab bar (<640px) — mirrors the mockup's #bottom-tabbar. */}
      <nav id="bottom-tabbar" aria-label="Primary navigation (mobile)">
        {(['dashboard', 'contacts', 'messages', 'reports', 'settings'] as const).map((k) => {
          const s = SURFACES.find((x) => x.key === k)!;
          return (
            <button
              key={k}
              className={`bottom-tab ${active === s.key ? 'active' : ''}`}
              onClick={() =>
                s.key === 'messages'
                  ? openMessages()
                  : s.external
                    ? window.location.assign(s.external)
                    : s.key === 'settings'
                      ? setSettingsOpen(true)
                      : setActive(s.key)
              }
            >
              <span
                className="ico"
                aria-hidden
                dangerouslySetInnerHTML={{ __html: ICONS[s.key] ?? '' }}
              />
              {s.key === 'messages' && unreadMessages ? (
                <span className="bottom-tab-badge" aria-label={`${unreadMessages} unread messages`}>
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              ) : null}
              {s.label}
            </button>
          );
        })}
      </nav>

      <div id="content-area">
        <div id="topstrip">
          <div className="search">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              placeholder="Search clients…"
              value={headerSearch}
              onChange={(e) => {
                const v = e.target.value;
                setHeaderSearch(v);
                // #48 - clearing the box (native ✕ or deleting to empty) refreshes the
                // list back to all contacts at once; other text applies after a short
                // pause (ehr#777).
                headerSearchRef.current.input(v);
              }}
              onKeyDown={(e) => e.key === 'Enter' && runHeaderSearch()}
              aria-label="Search clients"
            />
          </div>
          <div id="topstrip-right">
            <UserChip
              onNavigate={navigate}
              open={pickerOpen}
              setOpen={setPickerOpen}
              photo={myPhoto}
            />
            {/* ehr#252 - the bell lists this person's unread Alerts Hub notices; its footer
                keeps the org-wide Notification log one click away. */}
            {buttonShown(dashView, 'notifications') && (
              <NoticeBell
                overdueTasks={Boolean(overdueTasks)}
                onOpenLog={() => setActive('log')}
                onOpenSharedForm={(contactId, submissionId) =>
                  handleOpenContact(contactId, 'forms', submissionId)
                }
              />
            )}
            <div className="user-chip-wrap" ref={kebabRef} style={{ position: 'relative' }}>
              <button
                id="topstrip-dot-btn"
                aria-label="More options"
                title="More options"
                onClick={() => setKebabOpen((o) => !o)}
                aria-expanded={kebabOpen}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="19" r="1.5" fill="currentColor" stroke="none" />
                </svg>
              </button>
              {kebabOpen && (
                <div id="topstrip-dot-panel" role="menu">
                  <button
                    className="bv-dot-menu-item"
                    onClick={() => {
                      setMegaOpen(true);
                      setKebabOpen(false);
                    }}
                  >
                    {svg(
                      '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
                    )}
                    Manage Settings
                  </button>
                  <button
                    className="bv-dot-menu-item"
                    onClick={() => {
                      setNavEditing(true);
                      setKebabOpen(false);
                    }}
                  >
                    {svg(
                      '<line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>',
                    )}
                    Customize Nav
                  </button>
                  <button
                    className="bv-dot-menu-item"
                    onClick={() => {
                      setKebabOpen(false);
                      setActive('contacts');
                      // Let Contacts mount/route first, then open its Show Columns panel.
                      setTimeout(
                        () => window.dispatchEvent(new CustomEvent('credify:open-columns')),
                        60,
                      );
                    }}
                  >
                    {svg(
                      '<rect x="3" y="3" width="6" height="18" rx="1"/><rect x="13" y="3" width="3" height="18" rx="1"/><rect x="19" y="3" width="2" height="18" rx="1"/>',
                    )}
                    Columns
                  </button>
                  <div className="bv-dot-menu-divider" />
                  {buttonShown(dashView, 'export') && (
                    <button
                      className="bv-dot-menu-item"
                      onClick={() => {
                        setIeOpen(true);
                        setKebabOpen(false);
                      }}
                    >
                      {svg(
                        '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>',
                      )}
                      Import / Export
                    </button>
                  )}
                  <div className="bv-dot-menu-divider" />
                  <button
                    className="bv-dot-menu-item"
                    disabled={loggingOut}
                    onClick={() => void logOut()}
                  >
                    {svg(
                      '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>',
                    )}
                    {loggingOut ? 'Logging out…' : 'Log out'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {sessionView({ signedIn: !!user, loading }) === 'checking' ? (
          // ehr#665: a neutral loading state while the session is checked (a page load, e.g. back
          // from Messages or the Schedule); the Sign in card only once the check says signed out.
          <main className="content">
            <Loading />
          </main>
        ) : !user ? (
          <main className="content">
            <div className="card">
              <h2>Sign in</h2>
              <SignInScreen loading={loading} authStatus={authStatus} />
            </div>
          </main>
        ) : telnyxPath ? (
          // ehr#298: support sets an org's Telnyx number at /telnyx/<org-id>/<number>.
          <SupportTelnyxPage orgId={telnyxPath.orgId} number={telnyxPath.number} />
        ) : (
          <Surfaces
            key={user.id}
            active={active}
            contactsQuery={contactsQuery}
            onNavigate={navigate}
            openContact={openContact}
            onOpenContact={handleOpenContact}
            onOpenContactConsumed={() => setOpenContact(null)}
            openTaskId={openTaskId}
            onOpenGroupNote={(appointmentKey, focus) => setGroupLink({ appointmentKey, focus })}
            onOpenNote={openQueueNote}
            onWriteNote={openWriteNote}
            onQueue={(q) => setNotesBadge(badgeCount(q))}
            hubTab={hubTab}
          />
        )}
      </div>

      {ieOpen && <ImportExportModal category="client" onClose={() => setIeOpen(false)} />}
      {contactTypesOpen && <ContactTypesManager onClose={() => setContactTypesOpen(false)} />}
      {statusesOpen && <StatusesManager onClose={() => setStatusesOpen(false)} />}
      {taskStatusesOpen && <TaskStatusesManager onClose={() => setTaskStatusesOpen(false)} />}
      {actionsOpen && <StatusActionsModal onClose={() => setActionsOpen(false)} />}
      {matrixOpen && <AccessMatrixModal onClose={() => setMatrixOpen(false)} />}
      {myProfileOpen && user && (
        <ProviderProfileFrame
          userId={user.id}
          onClose={() => {
            setMyProfileOpen(false);
            setSigLink(false);
          }}
          onSaved={notifyPhotosChanged}
          editSignature={sigLink}
          onSignatureClosed={clearSignatureParam}
        />
      )}
      {usersOpen && (
        <UsersModal
          openProfileId={settingsLink?.key === 'users' ? settingsLink.user : undefined}
          onClose={() => {
            setUsersOpen(false);
            // ehr#232: opened by the Calendar's Add provider in a pop-up: closing closes it.
            if (settingsLink?.key === 'users') {
              if (settingsLink.popup && window.opener) window.close();
              clearSettingsParams();
            }
          }}
        />
      )}
      {touchRulesOpen && <TouchRulesModal onClose={() => setTouchRulesOpen(false)} />}
      {cptOpen && <CptCodesModal onClose={() => setCptOpen(false)} />}
      {configModal && (
        <ConfigListModal
          entity={configModal.entity}
          title={configModal.title}
          onClose={() => {
            setConfigModal(null);
            // Opened by the Calendar in its own pop-up window: closing the list closes it.
            if (settingsLink?.popup && window.opener) window.close();
            clearSettingsParams();
          }}
        />
      )}
      {megaOpen && (
        <SettingsMegaMenu
          onClose={() => setMegaOpen(false)}
          canManage={canManage(user?.role)}
          onFire={(action) => {
            if (action.kind === 'nav') {
              navigate(action.surface);
            } else if (action.kind === 'data') {
              setSettingsSection(action.section);
              setSettingsOpen(true);
            } else if (action.kind === 'config') {
              if (action.entity === 'contact-types') {
                setContactTypesOpen(true);
              } else if (action.entity === 'statuses') {
                setStatusesOpen(true);
              } else if (action.entity === 'task-statuses') {
                setTaskStatusesOpen(true);
              } else {
                setConfigModal({ entity: action.entity as ConfigEntity, title: action.title });
              }
            } else if (action.kind === 'actions') {
              setActionsOpen(true);
            } else if (action.kind === 'access-matrix') {
              setMatrixOpen(true);
            } else if (action.kind === 'touch-rules') {
              setTouchRulesOpen(true);
            } else if (action.kind === 'cpt-codes') {
              setCptOpen(true);
            } else {
              setSettingsShell({ name: action.name, note: action.note });
            }
          }}
        />
      )}
      {groupLink && (
        <GroupNotePage
          appointmentKey={groupLink.appointmentKey}
          focusContactId={groupLink.focus}
          onClose={() => {
            clearGroupParams();
            setGroupLink(null);
          }}
        />
      )}
      {settingsOpen && (
        <DataSettingsModal
          onClose={() => {
            setSettingsOpen(false);
            setSettingsSection(undefined);
            // Opened by the Calendar in its own pop-up window (calendar#33, #37): closing closes it.
            if (dataSettingsLink && settingsLink?.popup && window.opener) window.close();
            clearSettingsParams();
          }}
          initialSection={settingsSection}
        />
      )}
      {settingsShell && (
        <div className="modal-backdrop" onClick={() => setSettingsShell(null)}>
          <div
            className="settings-shell"
            role="dialog"
            aria-modal="true"
            aria-label={settingsShell.name}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{settingsShell.name}</h2>
            <p className="muted">{settingsShell.note ?? 'This settings page is being built.'}</p>
            <div className="settings-shell-foot">
              <button className="btn" onClick={() => setSettingsShell(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* messaging#24 - a new message arrived in Messaging while the EHR is open. No name or
          text: only that there is one, with the way there. */}
      {msgAlert && (
        <div className="msg-alert" role="status" aria-live="polite">
          <span className="msg-alert-dot" aria-hidden />
          <span>
            New message in Messaging
            {unreadMessages ? ` (${unreadMessages} unread)` : ''}
          </span>
          <button type="button" className="msg-alert-open" onClick={openMessages}>
            Open
          </button>
          <button
            type="button"
            className="msg-alert-x"
            aria-label="Dismiss"
            onClick={() => setMsgAlert(false)}
          >
            ×
          </button>
        </div>
      )}
      {/* ehr#279: last, so it sits above any window that's open when the sign-in runs out. */}
      {user && <SessionExpiredDialog />}
      {/* ehr#416: auto logout after the person's idle time (Data & settings > Auto logout) */}
      {user && <AutoLogoutGuard isProd={IS_PROD} onSignOut={signOutNow} />}
    </div>
  );
}
